import { Button } from "@/components/ui/button";
import { Check, ArrowRight, Users, Smartphone, QrCode, Link2, BarChart3, Zap, Star, Clock, UserPlus, Search, Phone, Mail, Globe, Tag, Briefcase, Building2, TrendingUp, Palette, Video } from "lucide-react";
import qcardLogo from "@/assets/qcard-logo-official.png";
import qcardHero from "@/assets/qcard-hero.png";
import companyLogos from "@/assets/company-logos.png";
import discoverQcard from "@/assets/discover-qcard.webp";

const HomepageFreeQCardV2 = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-b from-background via-background to-muted/30">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-primary/5 rounded-full blur-3xl" />
        </div>

        <div className="container relative z-10 mx-auto px-4 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              <img src={qcardLogo} alt="QCard" className="h-12 mb-6 mx-auto lg:mx-0" />
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6 border border-primary/20">
                <Zap className="w-4 h-4" />
                Now Free for Everyone
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                Create Your Free QCard —{" "}
                <span className="text-primary">The Digital Business Card That Gets You Remembered.</span>
              </h1>
              
              <p className="text-xl text-muted-foreground mb-8 max-w-xl mx-auto lg:mx-0">
                QCard is the Professional Engagement Platform that makes you unforgettable for each connection.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button size="lg" className="text-lg px-8 py-6 font-semibold">
                  Create Your Free QCard
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button size="lg" variant="outline" className="text-lg px-8 py-6 font-semibold">
                  See How It Works
                </Button>
              </div>
              
              <div className="mt-8 flex items-center gap-6 justify-center lg:justify-start text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-primary" />
                  Free forever
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-primary" />
                  60-second setup
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-primary" />
                  No credit card
                </div>
              </div>
            </div>

            {/* Right - Hero Image */}
            <div className="relative flex justify-center">
              <img 
                src={qcardHero} 
                alt="Professionals exchanging contact info with QCard" 
                className="w-full max-w-lg rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Used by Industry Leaders */}
      <section className="py-12 bg-background border-y border-border">
        <div className="container mx-auto px-4">
          <p className="text-center text-muted-foreground mb-8 text-sm font-medium">Used by Industry Leaders</p>
          <img 
            src={companyLogos} 
            alt="Trusted by Redfin, Keller Williams, UBS, S&P Global, Sotheby's, Compass, and more" 
            className="max-w-4xl mx-auto w-full opacity-80"
          />
        </div>
      </section>

      {/* Problem → Impact → Solution → Benefit */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              The Networking Problem <span className="text-primary">Nobody Talks About</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              You're losing business every day — not because you're not good enough, but because people forget you.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Problem */}
            <div className="bg-card rounded-2xl p-6 border border-border">
              <div className="w-12 h-12 rounded-xl bg-destructive/10 flex items-center justify-center mb-4">
                <span className="text-2xl">😰</span>
              </div>
              <h3 className="text-lg font-semibold mb-2 text-destructive">The Problem</h3>
              <p className="text-muted-foreground text-sm">
                After every meeting, event, or conversation — most people never save your contact info. Paper cards get tossed. Links get lost.
              </p>
            </div>

            {/* Impact */}
            <div className="bg-card rounded-2xl p-6 border border-border">
              <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center mb-4">
                <span className="text-2xl">📉</span>
              </div>
              <h3 className="text-lg font-semibold mb-2 text-orange-500">The Impact</h3>
              <p className="text-muted-foreground text-sm">
                You lose clients, referrals, and opportunities. Not because you weren't great — but because you vanished from their phone.
              </p>
            </div>

            {/* Solution */}
            <div className="bg-card rounded-2xl p-6 border border-border">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <span className="text-2xl">✨</span>
              </div>
              <h3 className="text-lg font-semibold mb-2 text-primary">QCard Solution</h3>
              <p className="text-muted-foreground text-sm">
                QCard instantly adds you into their Contacts app — complete with your photo, links, title, and keywords. One tap. Done.
              </p>
            </div>

            {/* Benefit */}
            <div className="bg-card rounded-2xl p-6 border border-border">
              <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center mb-4">
                <span className="text-2xl">🚀</span>
              </div>
              <h3 className="text-lg font-semibold mb-2 text-green-500">The Benefit</h3>
              <p className="text-muted-foreground text-sm">
                You stay remembered, searchable, and reachable. More follow-ups. More opportunities. More business.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Add to Contacts Feature */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-4">
              <UserPlus className="w-4 h-4" />
              Core Differentiator
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              <span className="text-primary">Add to Contacts</span> — Your Secret Weapon
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              QCard saves you directly into their phone's Contacts app. Here's exactly what gets added:
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* What Gets Saved */}
            <div className="space-y-4">
              {[
                { icon: Users, label: "Your Photo", desc: "They'll recognize your face instantly" },
                { icon: Briefcase, label: "Your Title & Company", desc: "Your professional identity, crystal clear" },
                { icon: Phone, label: "Phone Number", desc: "Direct line for quick follow-ups" },
                { icon: Mail, label: "Email Address", desc: "Professional communication ready" },
                { icon: Globe, label: "Website & Links", desc: "Your portfolio, LinkedIn, socials — all saved" },
                { icon: Tag, label: "Custom Keywords", desc: '"Real estate", "Consultant", "Miami" — they can search and find you' },
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-4 p-4 rounded-xl bg-card border border-border hover:border-primary/50 transition-colors">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold">{item.label}</h4>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Visual Process */}
            <div className="bg-card rounded-3xl border border-border p-8">
              <h3 className="text-xl font-semibold mb-8 text-center">How It Works</h3>
              <div className="space-y-6">
                {[
                  { step: "1", title: "Tap", desc: "They tap your QCard link or scan your QR code" },
                  { step: "2", title: "Add to Contacts", desc: "One button press adds you to their phone" },
                  { step: "3", title: "Saved Forever", desc: "You're in their contacts — permanently searchable" },
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground font-bold text-lg flex items-center justify-center flex-shrink-0">
                      {item.step}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold">{item.title}</h4>
                      <p className="text-sm text-muted-foreground">{item.desc}</p>
                    </div>
                    {index < 2 && (
                      <ArrowRight className="w-5 h-5 text-muted-foreground hidden md:block" />
                    )}
                  </div>
                ))}
              </div>
              
              <div className="mt-8 p-4 rounded-xl bg-primary/10 border border-primary/20">
                <p className="text-center text-sm">
                  <span className="font-semibold text-primary">Result:</span> Better follow-ups, more recognition, and you never get forgotten again.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Free QCard Section */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 text-green-600 text-sm font-semibold mb-4 border border-green-500/20">
                <Zap className="w-4 h-4" />
                100% Free Forever
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Everything You Need. <span className="text-primary">Completely Free.</span>
              </h2>
              <p className="text-lg text-muted-foreground">
                No trial. No credit card. No hidden fees. Just create your QCard and start connecting.
              </p>
            </div>

            <div className="bg-card rounded-3xl border border-border overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-primary to-primary/80 p-6 text-center">
                <p className="text-primary-foreground/80 text-sm mb-1">QCard Free</p>
                <div className="flex items-baseline justify-center gap-2">
                  <span className="text-5xl font-bold text-primary-foreground">$0</span>
                  <span className="text-primary-foreground/70">/forever</span>
                </div>
              </div>

              {/* Features */}
              <div className="p-8">
                <div className="grid sm:grid-cols-2 gap-4 mb-8">
                  {[
                    "Complete QCard profile",
                    "Photo, name, title & company",
                    "Phone, email & website links",
                    "All social media profiles",
                    "Instant Add to Contacts",
                    "Unlimited sharing",
                    "Personal QR code",
                    "Media & link sections",
                    "Personal URL (qcard.me/you)",
                  ].map((feature, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                        <Check className="w-4 h-4 text-green-500" />
                      </div>
                      <span className="text-foreground">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="text-center">
                  <Button size="lg" className="text-lg px-12 py-6 font-semibold">
                    Start Free
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                  <p className="mt-4 text-sm text-muted-foreground">
                    Set up in 60 seconds. No credit card required.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Professional Engagement Platform */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              More Than a Digital Card — <span className="text-primary">A Professional Engagement Platform</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              QCard is your modern, always-updated digital identity that builds trust instantly.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Palette,
                title: "Customizable Profile",
                desc: "Design your digital identity with custom colors, layouts, and branding."
              },
              {
                icon: Link2,
                title: "Links & Media",
                desc: "Showcase your work, portfolio, videos, and all your important links."
              },
              {
                icon: BarChart3,
                title: "Analytics & Insights",
                desc: "See who's viewing your profile and track engagement over time."
              },
              {
                icon: Users,
                title: "Team & Enterprise",
                desc: "Perfect for teams — manage multiple QCards from one dashboard."
              },
              {
                icon: QrCode,
                title: "QR Code Sharing",
                desc: "Share your profile instantly with a scannable QR code."
              },
              {
                icon: Smartphone,
                title: "Always Updated",
                desc: "Change your info once — everyone who has your QCard sees the update."
              },
            ].map((feature, index) => (
              <div key={index} className="bg-card rounded-2xl p-6 border border-border hover:border-primary/50 transition-colors group">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                <p className="text-muted-foreground text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof / Use Cases */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Trusted by Professionals <span className="text-primary">Across Industries</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              See how QCard helps people stand out and stay connected.
            </p>
          </div>

          {/* Use Cases */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {[
              { icon: Building2, industry: "Real Estate", quote: "My clients can save my info instantly. I've closed deals just because they could find me when they needed me." },
              { icon: Briefcase, industry: "Consulting", quote: "QCard replaced my paper cards entirely. It's professional, modern, and people actually remember me." },
              { icon: TrendingUp, industry: "Financial Advisors", quote: "Being in their contacts means I'm one call away. That's the difference between a lead and a client." },
              { icon: Video, industry: "Creators & Influencers", quote: "I share my QCard everywhere. It's my digital hub — all my links, content, and contact info in one place." },
              { icon: Users, industry: "Sales Teams", quote: "Our entire team uses QCard. We look professional and our follow-up rate has doubled." },
              { icon: Star, industry: "Entrepreneurs", quote: "First impressions matter. QCard gives me credibility before I even say a word." },
            ].map((item, index) => (
              <div key={index} className="bg-card rounded-2xl p-6 border border-border">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <item.icon className="w-5 h-5 text-primary" />
                  </div>
                  <span className="font-semibold">{item.industry}</span>
                </div>
                <p className="text-muted-foreground text-sm italic">"{item.quote}"</p>
              </div>
            ))}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { value: "60s", label: "Setup Time" },
              { value: "$0", label: "Cost Forever" },
              { value: "1 Tap", label: "To Save Contact" },
              { value: "∞", label: "Sharing Limit" },
            ].map((stat, index) => (
              <div key={index} className="text-center p-6 bg-card rounded-2xl border border-border">
                <div className="text-3xl md:text-4xl font-bold text-primary mb-2">{stat.value}</div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-24 bg-primary">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-primary-foreground mb-6">
            Create Your Free QCard — Be Remembered.
          </h2>
          <p className="text-xl text-primary-foreground/80 mb-8 max-w-2xl mx-auto">
            Stop losing connections. Start getting saved. Join thousands of professionals who never get forgotten.
          </p>
          <Button size="lg" variant="secondary" className="text-lg px-12 py-6 font-semibold">
            Get Your Free QCard Now
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          <p className="mt-6 text-sm text-primary-foreground/60">
            Free forever • 60-second setup • No credit card required
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-background border-t border-border">
        <div className="container mx-auto px-4 text-center">
          <p className="text-muted-foreground">
            © {new Date().getFullYear()} QCard. The Professional Engagement Platform.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default HomepageFreeQCardV2;
